/**
 * Payment methods — the one place BOTH apps list them (docs/ENUMS.md). Moved here from
 * apps/contractor-app/src/constants/paymentMethods.js on 2026-10-29 so the customer portal can use
 * the same labels; that file now only re-exports this one.
 *
 * PAYMENT_METHOD is the payments.payment_method CHECK set, verified live 2026-10-29:
 *   cash, check, credit_card, debit_card, bank_transfer, online, other
 * record_invoice_payment_rpc refuses anything else with "Invalid payment method".
 *
 * PAYMENT_METHOD_CHOICES are the ways a contractor can say they were paid when recording a
 * payment by hand. Several have no column value of their own (ACH, wire, PayPal, Venmo, Zelle),
 * so each choice names the stored `method`; when a choice is narrower than what it is stored as
 * (`keepDetail`), resolvePaymentMethodChoice also returns the choice key, which callers save in
 * payments.metadata.method_detail so reports can still tell a Zelle payment from other "other"s.
 */
export const PAYMENT_METHOD = {
  CASH: 'cash',
  CHECK: 'check',
  CREDIT_CARD: 'credit_card',
  DEBIT_CARD: 'debit_card',
  BANK_TRANSFER: 'bank_transfer',
  ONLINE: 'online',
  OTHER: 'other'
};

export const PAYMENT_METHOD_VALUES = Object.values(PAYMENT_METHOD);

export const PAYMENT_METHOD_CHOICES = {
  cash: { method: PAYMENT_METHOD.CASH, label: 'Cash' },
  check: { method: PAYMENT_METHOD.CHECK, label: 'Check' },
  credit_card: { method: PAYMENT_METHOD.CREDIT_CARD, label: 'Credit Card' },
  debit_card: { method: PAYMENT_METHOD.DEBIT_CARD, label: 'Debit Card' },
  ach: { method: PAYMENT_METHOD.BANK_TRANSFER, label: 'ACH/Bank Transfer', keepDetail: true },
  wire_transfer: { method: PAYMENT_METHOD.BANK_TRANSFER, label: 'Wire Transfer', keepDetail: true },
  paypal: { method: PAYMENT_METHOD.OTHER, label: 'PayPal', keepDetail: true },
  venmo: { method: PAYMENT_METHOD.OTHER, label: 'Venmo', keepDetail: true },
  zelle: { method: PAYMENT_METHOD.OTHER, label: 'Zelle', keepDetail: true },
  other: { method: PAYMENT_METHOD.OTHER, label: 'Other' }
};

/**
 * Choice key -> { payment_method, method_detail }. payment_method is always a PAYMENT_METHOD
 * value; method_detail is the choice key for keepDetail choices, else null. Unknown key -> null.
 */
export const resolvePaymentMethodChoice = (choice) => {
  const key = String(choice || '').trim().toLowerCase();
  const entry = PAYMENT_METHOD_CHOICES[key];
  if (!entry) return null;
  return { payment_method: entry.method, method_detail: entry.keepDetail ? key : null };
};

/** Display label for a stored payments.payment_method value. */
export const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHOD.CASH]: 'Cash',
  [PAYMENT_METHOD.CHECK]: 'Check',
  [PAYMENT_METHOD.CREDIT_CARD]: 'Credit Card',
  [PAYMENT_METHOD.DEBIT_CARD]: 'Debit Card',
  [PAYMENT_METHOD.BANK_TRANSFER]: 'Bank Transfer',
  [PAYMENT_METHOD.ONLINE]: 'Online',
  [PAYMENT_METHOD.OTHER]: 'Other'
};

// Short spellings older pickers send (SendInvoiceModal / CompletionAndInvoiceModal send 'card').
const PAYMENT_METHOD_ALIASES = {
  card: PAYMENT_METHOD.CREDIT_CARD,
  credit: PAYMENT_METHOD.CREDIT_CARD,
  bank: PAYMENT_METHOD.BANK_TRANSFER
};

/**
 * Any method spelling the app produces -> the stored payments.payment_method value, or null when
 * it is not a known method. Accepts stored values, PAYMENT_METHOD_CHOICES keys and the aliases.
 */
export const normalizePaymentMethod = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (PAYMENT_METHOD_VALUES.includes(key)) return key;
  if (PAYMENT_METHOD_CHOICES[key]) return PAYMENT_METHOD_CHOICES[key].method;
  return PAYMENT_METHOD_ALIASES[key] || null;
};

/**
 * What to call a recorded payment's method wherever it is shown (invoice ledger, customer portal
 * payment history, receipts): the method the contractor PICKED (metadata.method_detail, e.g.
 * 'zelle' -> "Zelle"), else the stored payments.payment_method label ('other' -> "Other"), else
 * null when neither is known. Accepts a payments row with `metadata`, or a row that selected
 * the detail on its own as `method_detail`.
 */
export const paymentMethodLabel = (payment) => {
  const detail = String(payment?.method_detail ?? payment?.metadata?.method_detail ?? '').trim().toLowerCase();
  if (detail && PAYMENT_METHOD_CHOICES[detail]) return PAYMENT_METHOD_CHOICES[detail].label;
  const method = normalizePaymentMethod(payment?.payment_method);
  return method ? PAYMENT_METHOD_LABELS[method] : null;
};
